import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import type { MerchantSubscription, ApiResponse } from '../types';

/**
 * Hook to fetch merchant subscription status
 */
export function useSubscription() {
  const authenticatedFetch = useAuthenticatedFetch();
  const [subscription, setSubscription] = useState<MerchantSubscription | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch('/api/billing/subscription');

      // 404 means no subscription - this is a valid state, not an error
      if (response.status === 404) {
        setSubscription(null);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json: ApiResponse<MerchantSubscription> = await response.json();

      if (json.success && json.data) {
        setSubscription(json.data);
      } else {
        throw new Error(json.error || 'Unknown API error');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch subscription';
      setError(message);
      console.error('Subscription fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { subscription, loading, error, refetch: fetchData };
}
